import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute } from '@angular/router';
import {
  ColumnApi,
  DisplayedColumnsChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import { merge, Subject, take, takeUntil } from 'rxjs';

import { PaginationInterface } from '@app/interfaces/pagination.model';
import { inline_ } from '@app/shared/utils/string.utils';
import { ReceivingReportUnifiedFormComponent } from 'v2/modules/receiving-report/components/receiving-report-unified-form/receiving-report-unified-form.component';
import { DialogConfig } from 'v2/modules/shared/configs/dialog.config';
import { AgGridUtils } from 'v2/modules/shared/util/ag-grid.utils';
import { LumResponseError } from 'v2/shared/api/lum-response-error';
import { LumSnackBar } from 'v2/shared/lum-snack-bar/lum-snack-bar.service';

import {
  CostLayerChangesLogColToField,
  COST_LAYER_CHANGES_LOG_DEFAULT_GRID_OPTIONS,
} from '../../config/cost-layer-changes-log.config';
import {
  CostLayerChangesParamsBySubset,
  ECostLayerChangesLogLayout,
  ECostLayerChangesSubset,
} from '../../cost-layers.const';
import { ICostLayer, ICostLayerChange } from '../../cost-layers.types';
import { CostLayersApiService } from '../../services/cost-layers-api.service';
import {
  CostLayersViewSettingsService,
  ICostLayersViewSettingsState,
} from '../../services/cost-layers-view-settings.service';
import { CostLayersDataStore } from '../../services/cost-layers.data-store';

const _DEFAULT_SUBSET = ECostLayerChangesSubset.QUANTITY;

const _LogLayoutByChangesSubset :Record<
  ECostLayerChangesSubset,
  ECostLayerChangesLogLayout
> = {
  [ECostLayerChangesSubset.ALL]: ECostLayerChangesLogLayout.AUDIT,
  [ECostLayerChangesSubset.QUANTITY]: ECostLayerChangesLogLayout.SALES,
} as const;

// changes query param
const _CHANGES_PARAM_NAME = 'changes';

type _ChangesParamValue = 'all' | null;
const _isChangesParamValue = (candidate :any) :candidate is _ChangesParamValue =>
  ['all', null].includes(candidate);

type _ChangesParamsHash = {
  [_CHANGES_PARAM_NAME] :_ChangesParamValue;
};

const _ChangesParamsBySubset :Record<ECostLayerChangesSubset, _ChangesParamsHash> =
  {
    [ECostLayerChangesSubset.ALL]: { [_CHANGES_PARAM_NAME]: 'all' },
    [ECostLayerChangesSubset.QUANTITY]: { [_CHANGES_PARAM_NAME]: null },
  } as const;
const _ChangesParamValueToSubsetMap :Map<
  _ChangesParamValue,
  ECostLayerChangesSubset
> = new Map(
  Object.entries(_ChangesParamsBySubset).map(
    ([subset, params] :[ECostLayerChangesSubset, _ChangesParamsHash]) :[
      _ChangesParamValue,
      ECostLayerChangesSubset,
    ] => [params[_CHANGES_PARAM_NAME], subset],
  ),
);

// view settings
type _SettingStoringColumnStateArr =
  | 'costLayerLogColumnsForAll'
  | 'costLayerLogColumnsForQuantity';

@Component({
  selector: 'lum-cost-layer-info',
  templateUrl: './cost-layer-info.component.html',
  styleUrls: ['./cost-layer-info.component.scss'],
})
export class CostLayerInfoComponent implements OnDestroy, OnInit {
  readonly #cancelLoad$ = new Subject<void>();
  readonly #destroy$ = new Subject<void>();

  #agGridApi :GridApi<ICostLayerChange>;
  #agColumnApi :ColumnApi;
  #isSwitchingLayouts :boolean = false;

  readonly _ChangesParamsBySubset = _ChangesParamsBySubset;
  readonly ECostLayerChangesLogLayout = ECostLayerChangesLogLayout;
  readonly ECostLayerChangesSubset = ECostLayerChangesSubset;

  agGridOptions :GridOptions = {
    ...COST_LAYER_CHANGES_LOG_DEFAULT_GRID_OPTIONS,
    suppressPaginationPanel: true,
  };
  currentChangesLayout :ECostLayerChangesLogLayout =
    _LogLayoutByChangesSubset[_DEFAULT_SUBSET];
  currentChangesSubset :ECostLayerChangesSubset = _DEFAULT_SUBSET;
  paging :PaginationInterface = null;

  constructor(
    private _api :CostLayersApiService,
    private _cd :ChangeDetectorRef,
    private _legacyDialog :MatLegacyDialog,
    private _route :ActivatedRoute,
    private _snackBar :LumSnackBar,
    private _viewSettings :CostLayersViewSettingsService,
    public store :CostLayersDataStore,
  ) {}

  ngOnInit() {
    this.#subscribeToRoute();
    this.#subscribeToStore();
  }
  ngOnDestroy() {
    this.#cancelLoad$.complete();
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  handlePaginationChanged() {
    const pageIndex = this.#agGridApi?.paginationGetCurrentPage();
    const pageSize = this.#agGridApi?.paginationGetPageSize();
  }

  handleReceiptClick(e :PointerEvent, receiptId :number) {
    e.preventDefault();

    this._legacyDialog.open(ReceivingReportUnifiedFormComponent, {
      ...DialogConfig.XX_LARGE,
      data: { id: receiptId },
    });
  }

  handleGridReady(e :GridReadyEvent) {
    this.#agGridApi = e.api;
    this.#agColumnApi = e.columnApi;

    const id = this.store.get('viewingCostLayer')?.id;

    if (id) {
      this.#agGridApi?.setServerSideDatasource(
        this.#getChangesServerSideDatasource(id),
      );
    }

    this.#initSyncViewSettingsToGrid();
  }

  #buildQueryParams(params :IServerSideGetRowsParams) :HttpParamsHash {
    // AG Grid handles paging, but it fetches the
    const paginationPageSize :number = this.agGridOptions.cacheBlockSize ?? 10;

    return {
      ...AgGridUtils.mapQueryParams(
        { params, paginationPageSize },
        {
          defaultSortField: 'createdAt', // instead of global default `created_at`
          getBackendSortKeyByColId: (colId) => CostLayerChangesLogColToField[colId],
          multiSort: true,
        },
      ),
      ...CostLayerChangesParamsBySubset[this.currentChangesSubset],
    };
  }

  #getChangesServerSideDatasource(id :ICostLayer['id']) :IServerSideDatasource {
    return {
      getRows: (getRowsParams :IServerSideGetRowsParams) => {
        this.#cancelLoad$.next();

        const params = this.#buildQueryParams(getRowsParams);

        this._api
          .getChanges(id, params)
          .pipe(take(1), takeUntil(merge(this.#cancelLoad$, this.#destroy$)))
          .subscribe({
            next: (response) =>
              getRowsParams.success({
                rowData: response.data,
                rowCount: response.meta?.total ?? null,
              }),
            error: (error :LumResponseError) => {
              getRowsParams.fail();
              this._snackBar.openError(error.message);
            },
          });
      },
    };
  }

  #getCurrentColumnsViewSettingName() :_SettingStoringColumnStateArr {
    const _ViewSettingNameBySubset :Record<
      ECostLayerChangesSubset,
      _SettingStoringColumnStateArr
    > = {
      [ECostLayerChangesSubset.ALL]: 'costLayerLogColumnsForAll',
      [ECostLayerChangesSubset.QUANTITY]: 'costLayerLogColumnsForQuantity',
    };

    return _ViewSettingNameBySubset[this.currentChangesSubset];
  }

  #initSyncViewSettingsToGrid() {
    if (!this.#agGridApi) {
      throw new Error(inline_`Cannot call '#initSyncViewSettingsToGrid()' when
        this.#agGridApi is falsey.`);
    }
    if (!this.#agColumnApi) {
      throw new Error(inline_`Cannot call '#initSyncViewSettingsToGrid()' when
        this.#agColumnApi is falsey.`);
    }

    // ensure we start synced
    this.#updateAgColumnStateFromViewSettings();

    // listen for column changes
    this.#agGridApi.addEventListener(
      'displayedColumnsChanged',
      (e :DisplayedColumnsChangedEvent) => {
        if (this.#isSwitchingLayouts) {
          // load the state for the current layout
          this.#updateAgColumnStateFromViewSettings();
          this.#isSwitchingLayouts = false;
        } else {
          // store the changed column state
          const setting = this.#getCurrentColumnsViewSettingName();

          this._viewSettings.updateState({
            [setting]: this.#agColumnApi.getColumnState(),
          });
        }
      },
    );
  }

  #subscribeToRoute() {
    this._route.queryParamMap
      .pipe(takeUntil(this.#destroy$))
      .subscribe((params) => {
        const changes = params.get(_CHANGES_PARAM_NAME) ?? null;

        if (!_isChangesParamValue(changes)) {
          throw new Error(
            `Unrecognized 'changes' query param value: ${JSON.stringify(changes)}`,
          );
        }

        this.#isSwitchingLayouts = true;

        this.currentChangesSubset = _ChangesParamValueToSubsetMap.get(changes);
        this.currentChangesLayout =
          _LogLayoutByChangesSubset[this.currentChangesSubset];

        const id = this.store.get('viewingCostLayer')?.id;

        if (id) {
          this.#agGridApi?.refreshServerSide({ purge: true });
        }

        this._cd.markForCheck();
      });
  }
  #subscribeToStore() {
    this.store.viewingCostLayer$
      .pipe(takeUntil(this.#destroy$))
      .subscribe((newViewingCostLayer) => {
        if (newViewingCostLayer?.id) {
          this.#agGridApi?.setServerSideDatasource(
            this.#getChangesServerSideDatasource(newViewingCostLayer.id),
          );
        }
      });
  }

  #updateAgColumnStateFromViewSettings() {
    const setting = this.#getCurrentColumnsViewSettingName();
    const state = this._viewSettings.get(setting);

    if (state) {
      this.#agColumnApi?.applyColumnState({ state, applyOrder: true });
    }
  }
}
